import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";
import { normalizeSowingInstructionItem } from "./sowingInstructionRequest";
import { randomId } from "@/shared/utils/randomId";

export interface PagedSowingInstructions {
  items: SowingInstruction[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export interface SowingInstruction {
  id: string;
  instructionNumber?: string;
  orderId?: string;
  orderNumber?: string;
  cropTypeId?: string;
  cropTypeName?: string;
  orderItemId?: string;
  customerName?: string;
  seedBatchId?: string;
  seedBatchNumber?: string;
  seedBatchAvailableQuantity?: number | null;
  seedBatchUnit?: string | null;
  seedSource?: number;
  variety: string;
  quantityToSow: number;
  quantitySown?: number;
  quantityGerminated?: number;
  quantityGrowing?: number;
  quantityReady?: number;
  unit: number;
  pricingMode?: number;
  pricingModeName?: string;
  trayType?: number;
  trayTypeName?: string;
  greenhouseId: string;
  greenhouseName?: string;
  greenhouseBedId?: string;
  bedNumber?: string;
  status: number;
  germinationChamberId?: string;
  scheduledSowingDate: string;
  scheduledStartDate?: string;
  scheduledEndDate?: string;
  expectedGerminationDate: string;
  actualSowingDate?: string;
  germinationStartedDate?: string;
  assignedToUserId: string;
  assignedToUserName?: string;
  notes?: string;
  createdAt: string;
  /** Latest requisition status for the linked sowing instruction (RequisitionStatus enum). Null when no requisition exists. */
  latestRequisitionStatus?: number | null;
  /** True when the latest requisition exists but does not include the instruction's seed batch line. */
  latestRequisitionMissingSeedBatch?: boolean;
  stockQuantity?: number;
  fillingReserve?: number;
}

export interface SowingInstructionDto {
  id: string;
  instructionNumber: string;
  issueDate: string;
  orderId?: string;
  orderNumber?: string;
  orderItemId?: string;
  seedBatchId?: string;
  seedBatchNumber?: string;
  seedBatchAvailableQuantity?: number | null;
  seedBatchUnit?: string | null;
  cropTypeId: string;
  cropTypeName: string;
  variety?: string;
  varietyPricingId?: string;
  quantityToSow: number;
  unit: number;
  productionBatchNumber?: string;
  greenhouseId?: string;
  greenhouseName?: string;
  greenhouseBedId?: string;
  bedNumber?: string;
  sowingLocation?: string;
  scheduledSowingDate: string;
  scheduledStartDate?: string;
  scheduledEndDate?: string;
  actualSowingDate?: string;
  expectedGerminationDate: string;
  actualGerminationDate?: string;
  assignedToUserId?: string;
  assignedToUserName?: string;
  status: string;
  statusNotes?: string;
  quantitySown?: number;
  quantityGerminated?: number;
  germinationRate?: number;
  germinationGrade?: string;
  isPoorGermination: boolean;
  isGoodGermination: boolean;
  requiresApproval: boolean;
  isApproved: boolean;
  approvedAt?: string;
  specialInstructions?: string;
  notes?: string;
  isRevoked: boolean;
  revokedAt?: string;
  revocationReason?: string;
  purpose?: number;
  pricingMode?: number;
  pricingModeName?: string;
  trayType?: number;
  trayTypeName?: string;
  stockQuantity?: number;
  germinationChamberId?: string;
  fillingReserve?: number;
  createdAt: string;
  updatedAt?: string;
  createdBy: string;
}

export interface RequiredInput {
  inputId: string;
  quantityNeeded: number;
  unit: number;
  notes?: string;
}

export interface CreateSowingInstructionRequest {
  orderItemId?: string;
  seedBatchId?: string;
  purpose: number;
  seedSource: number;
  cropTypeId: string;
  variety: string;
  varietyPricingId?: string;
  quantityToSow: number;
  unit: string;
  trayType?: number;
  germinationChamberId?: string;
  greenhouseId?: string;
  greenhouseBedId?: string;
  sowingLocation?: string;
  scheduledSowingDate: string;
  expectedGerminationDate: string;
  assignedToUserId: string;
  specialInstructions?: string;
  notes?: string;
}

export interface SowingInstructionItem {
  orderItemId?: string;
  seedBatchId?: string;
  purpose: number;
  seedSource: number;
  cropTypeId: string;
  variety: string;
  varietyPricingId?: string;
  quantityToSow: number;
  unit: number;
  trayType?: number;
  germinationChamberId?: string;
  greenhouseId?: string;
  greenhouseBedId?: string;
  sowingLocation?: string;
  scheduledSowingDate: string;
  expectedGerminationDate: string | null;
  assignedToUserId: string | null;
  specialInstructions?: string;
  notes?: string;
  requiredInputs: RequiredInput[];
}

export interface CreateBatchSowingInstructionRequest {
  items: SowingInstructionItem[];
  notes?: string;
}

export interface ApproveSowingInstructionRequest {
  sowingInstructionId: string;
  approved: boolean;
  notes?: string;
}

export interface RecordSowingItemUsage {
  requisitionItemId: string;
  quantityUsed: number;
  quantityReturned: number;
  unitOfMeasure: number;
  notes?: string;
}

export interface RecordSowingRequest {
  sowingInstructionId: string;
  actualSowingDate: string;
  quantitySown: number;
  quantitySownUnitOfMeasure?: number;
  seedsUsedQuantity?: number;
  seedsUsedUnitOfMeasure?: number;
  seedsReturnedQuantity?: number;
  seedsReturnedUnitOfMeasure?: number;
  bedIds?: string[];
  chamberPlacements?: { germinationChamberId: string; traysPlaced: number }[];
  itemUsages?: RecordSowingItemUsage[];
  notes?: string;
}

export interface RecordGerminationRequest {
  sowingInstructionId: string;
  germinationStartedDate: string;
  germinationCompletedDate: string;
  quantityGerminated: number;
  quantityGerminatedUnitOfMeasure?: number;
  greenhouseId?: string;
  greenhouseBedId?: string;
  notes?: string;
}

export interface RecordGrowthRequest {
  sowingInstructionId: string;
  actualGrowthDate: string;
  quantityGrowing: number;
  quantityGrowingUnitOfMeasure?: number;
  greenhouseId?: string;
  notes?: string;
}

export interface RecordReadyForDispatchRequest {
  sowingInstructionId: string;
  actualReadyDate: string;
  quantityReady: number;
  quantityReadyUnitOfMeasure?: number;
  notes?: string;
}

export interface RecordHarvestRequest {
  sowingInstructionId: string;
  actualHarvestDate: string;
  quantityHarvested: number;
  quantityHarvestedUnitOfMeasure: number;
  notes?: string;
}

export interface MarkOvergrownRequest {
  sowingInstructionId: string;
  reason: string;
  quantityOvergrown?: number;
  notes?: string;
}

export interface PutOnHoldRequest {
  sowingInstructionId: string;
  reason: string;
  expectedResumeDate: string;
  notes?: string;
}

export interface ResumeFromHoldRequest {
  sowingInstructionId: string;
  resumeToStatus: string;
  notes?: string;
}

export interface RevokeSowingInstructionRequest {
  sowingInstructionId: string;
  reason: string;
}

// ─── Phase-workflow types (SI Overhaul) ─────────────────────────────────────

export type TrayType = 210 | 442;

export interface SalesSowingInstructionRequest {
  orderItemId?: string;
  orderId?: string;
  seedBatchId?: string;
  cropTypeId: string;
  varietyPricingId?: string;
  variety: string;
  trayType?: TrayType;
  pricingMode?: number;
  quantityToSow: number;
  unit: number;          // UnitOfMeasure enum: 11 = Trays, 2 = Pieces
  purpose: number;       // 0 = CustomerOrder, 1 = CompanyStock
  seedSource: number;    // 1 = CompanyInventory, 2 = CustomerProvided
  scheduledStartDate: string;
  scheduledEndDate: string;
  notes?: string;
}

export interface UpdateSowingInstructionRequest {
  id: string;
  scheduledSowingDate?: string;
  scheduledStartDate?: string;
  scheduledEndDate?: string;
  expectedGerminationDate?: string;
  notes?: string;
  /** Adjust planned quantity. Backend allows this only while the SI is in Draft or Pending. */
  quantityToSow?: number;
}

export interface SubmitSowingInstructionRequest {
  sowingInstructionId: string;
  notes?: string;
}

export interface AcceptSowingInstructionRequest {
  sowingInstructionId: string;
  specialInstructions?: string;
  notes?: string;
}

/** Per-bed tray allocation. Sum of trayCount must equal the batch total being placed. */
export interface BedTrayAllocation {
  bedId: string;
  trayCount: number;
}

export interface AcknowledgeReceiptRequest {
  sowingInstructionId: string;
  greenhouseId: string;
  /** Per-bed tray allocations. Empty = no specific beds. Sum of trayCount must equal quantityGrowing. */
  bedAllocations: BedTrayAllocation[];
  expectedReadyDate?: string;
  quantityGrowing?: number;
  notes?: string;
}

export interface GerminationSplitPreviewDto {
  quantityGerminated: number;
  stockQuantity: number;
  fillingReserve: number;
  ratioUsed: number;
  isZeroStock: boolean;
  warning?: string;
}

export interface GerminationChamberDto {
  id: string;
  chamberName: string;
  chamberCode: string;
  description?: string;
  maxCapacity?: number;
  isActive: boolean;
}

export interface GreenhouseStockEntryDto {
  id: string;
  sowingInstructionId: string;
  instructionNumber: string;
  cropTypeId: string;
  cropTypeName: string;
  variety: string;
  trayType: string;
  totalQuantity: number;
  fillingReserve: number;
  reservedQuantity: number;
  dispatchedQuantity: number;
  availableQuantity: number;
  reservedForOrderId?: string;
  reservedForOrderNumber?: string;
  greenhouseId?: string;
  greenhouseName?: string;
  /** Bay within the greenhouse the batch is placed in (e.g. "Bay A"). Null when no bed
   * is recorded against the entry. Surfaced on the dispatch picker so operators can
   * locate stock precisely (Greenhouse · Bay · Bed). */
  bayName?: string | null;
  /** Bed number on the primary bed (e.g. "Bed 01"). Null when no bed is recorded. */
  bedNumber?: string | null;
  /** Every (Bay, Bed) location this batch is placed across. A batch spanning multiple
   * beds (or even multiple bays) lists each one so dispatch can collect from every spot.
   * Empty when no beds are recorded; ordered by bay then bed number. */
  allBedLocations?: StockBedLocationDto[];
  germinationChamberId?: string;
  chamberName?: string;
  entryDate: string;
  expectedReadyDate?: string;
  isActive: boolean;
  isReadyForDispatch: boolean;
}

/** One (Bay, Bed) location a batch is placed in. */
export interface StockBedLocationDto {
  bayName: string;
  bedNumber: string;
}

export interface AvailableGreenhouseStockDto {
  isCompanyStock?: boolean;
  entryId: string;
  cropTypeId?: string;
  cropTypeName: string;
  /** Master crop classification: true = Agroforestry, false = Vegetable Seedlings. */
  isAgroforestry: boolean;
  variety?: string;
  /** Customer name if sown for an order, or "Standalone" for company seeds. */
  customerName: string;
  /** Human-readable label e.g. "210-hole" or "442-hole". Null for per-seedling stock. */
  trayType?: string | null;
  availableQuantity: number;
  /** Server-calculated physical pieces available on this source, after reservations. */
  availablePhysicalPieces?: number;
  totalQuantity: number;
  reservedQuantity: number;
  /** 30% backfill reserve held back from sale. May be released to TotalQuantity via the reserve-release workflow. */
  fillingReserve: number;
  /** Unit of measure for the quantities on this entry (e.g. "Trays" or "Pieces"). */
  stockUnit: string;
  greenhouseName?: string;
  /** Bay within the greenhouse the batch is placed in (e.g. "Bay A"). Surfaced on the
   * dispatch picker so the team can locate stock precisely. Null when no bay is recorded. */
  bayName?: string | null;
  /** Bed number / tray identifier within the bay (e.g. "Bed 01"). Null when no specific bed
   * is recorded (germination-chamber stock, legacy entries). */
  bedNumber?: string | null;
  chamberName?: string;
  entryDate: string;
  expectedReadyDate?: string;
  /** Linked sowing wave date, or a legacy instruction's recorded actual date. Null when no sowing date is linked. */
  sowingDate?: string;
  /** PricingMode enum value or serialized name: 1 = PerTray, 2 = PerSeedlingOrPiece. */
  pricingMode: number | "PerTray" | "PerSeedlingOrPiece";
  /** Raw hole count (210 or 442). Null for per-seedling stock. */
  trayTypeInt?: number | null;
  /** Buffer-adjusted sellable seedlings per physical tray. Null for per-seedling stock. */
  sellablePerTray?: number | null;
  /** Total sellable seedlings available (AvailableQuantity × SellablePerTray for tray stock). */
  availableSeedlings: number;
  /** Tray quantity committed to a Pending dispatch (still in AvailableQuantity, will be deducted on confirmation). */
  pendingDispatchQuantity: number;
  /** Stock source: "Greenhouse" or "Agroforestry". */
  source: string;
  /** Whether this batch is marked ready for dispatch/sale. */
  isReadyForDispatch: boolean;
  /** Active orders reserving stock on this entry. */
  reservationOrders?: GreenhouseEntryReservationOrderDto[];
}

export interface GreenhouseEntryReservationOrderDto {
  orderId: string;
  orderNumber: string;
  customerName: string;
  orderStatus: string;
  reservedQuantity: number;
}

export interface PagedGreenhouseStock {
  items: AvailableGreenhouseStockDto[];
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
}

export interface RecordMaterialUsageRequest {
  usageDate: string;
  departmentId: string;
  greenhouseId?: string;
  farmId?: string;
  farmName?: string;
  fieldId?: string;
  fieldName?: string;
  sowingInstructionId?: string;
  activityTypeId?: string;
  activityType: string;
  activityTypeOtherDescription?: string;
  purpose: string;
  sourceReferenceType?: string;
  sourceReferenceId?: string;
  sourceReferenceNumber?: string;
  notes?: string;
  items: MaterialUsageItemRequest[];
}

export interface MaterialUsageItemRequest {
  inputId: string;
  inputBatchId?: string;
  quantityUsed: number;
  unit: number;
}

export interface SowingMaterialUsageLogDto {
  id: string;
  usageDate: string;
  departmentId: string;
  departmentName: string;
  greenhouseId?: string;
  greenhouseName?: string;
  farmId?: string;
  farmName?: string;
  fieldId?: string;
  fieldName?: string;
  sowingInstructionId?: string;
  activityTypeId?: string;
  activityType: string;
  activityTypeOtherDescription?: string;
  purpose: string;
  recordedByUserId: string;
  recordedByUserName: string;
  recordedAt: string;
  sourceReferenceType?: string;
  sourceReferenceId?: string;
  sourceReferenceNumber?: string;
  notes?: string;
  linkedRequisitionId?: string;
  linkedStockIssueId?: string;
  reconciliationStatus: string;
  warnings: string[];
  items: SowingMaterialUsageItemDto[];
}

export interface MaterialUsageActivityType {
  id: string;
  code: string;
  name: string;
  description?: string;
  requiresMaterial: boolean;
  isActive: boolean;
  sortOrder: number;
}

export interface MaterialUsageLookupOption {
  id: string;
  code: string;
  name: string;
  description?: string;
}

export interface SowingMaterialUsageItemDto {
  id: string;
  inputId: string;
  inputCode: string;
  inputName: string;
  inputBatchId?: string;
  batchNumber?: string;
  quantityUsed: number;
  unit: string;
  stockBefore?: number;
  stockAfter?: number;
  wentBelowZero: boolean;
  varianceQuantity?: number;
  warningMessage?: string;
}

export const sowingInstructionsApi = {
  createSowingInstruction: async (
    payload: CreateSowingInstructionRequest,
    idempotencyKey: string = randomId()
  ): Promise<SowingInstruction> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstruction>>(
      "/SowingInstructions",
      payload,
      { headers: { "Idempotency-Key": idempotencyKey } }
    );
    return data.data;
  },

  createBatchSowingInstructions: async (
    payload: CreateBatchSowingInstructionRequest,
    idempotencyKey: string = randomId()
  ): Promise<SowingInstruction[]> => {
    const request = { ...payload, items: payload.items.map(normalizeSowingInstructionItem) };
    const { data } = await apiClient.post<ApiResponse<{ sowingInstructions: SowingInstruction[] } | SowingInstruction[]>>(
      "/SowingInstructions/batch",
      request,
      { headers: { "Idempotency-Key": idempotencyKey } }
    );
    return Array.isArray(data.data) ? data.data : data.data.sowingInstructions;
  },

  getAllSowingInstructions: async (): Promise<SowingInstruction[]> => {
    const { data } = await apiClient.get<ApiResponse<SowingInstruction[]>>(
      "/SowingInstructions"
    );
    return data.data;
  },

  updateSowingInstruction: async (
    payload: UpdateSowingInstructionRequest
  ): Promise<SowingInstruction> => {
    const { data } = await apiClient.put<ApiResponse<SowingInstruction>>(
      `/SowingInstructions/${payload.id}`,
      payload
    );
    return data.data;
  },

  getSowingInstructionsByStatus: async (
    status: number
  ): Promise<SowingInstruction[]> => {
    const { data } = await apiClient.get<ApiResponse<SowingInstruction[]>>(
      `/SowingInstructions/status/${status}`
    );
    return data.data;
  },

  // Backed by GET /SowingInstructions/paged — server slices the list and returns
  // PagedResult so the table doesn't have to load every instruction up-front.
  getPagedSowingInstructionsByStatuses: async (
    statuses: number[],
    pageNumber: number,
    pageSize: number,
    search?: string,
    includeUnfinishedSowing = false,
    includeUngerminatedSowings = false,
    requirePhysicalGreenhouseStock = false
  ): Promise<PagedSowingInstructions> => {
    const params = new URLSearchParams();
    statuses.forEach((s) => params.append("statuses", String(s)));
    params.set("pageNumber", String(pageNumber));
    params.set("pageSize", String(pageSize));
    if (search && search.trim()) params.set("search", search.trim());
    if (includeUnfinishedSowing) params.set("includeUnfinishedSowing", "true");
    if (includeUngerminatedSowings) params.set("includeUngerminatedSowings", "true");
    if (requirePhysicalGreenhouseStock) params.set("requirePhysicalGreenhouseStock", "true");
    const { data } = await apiClient.get<ApiResponse<PagedSowingInstructions>>(
      `/SowingInstructions/paged?${params.toString()}`
    );
    return data.data;
  },

  getSowingInstructionsByOrderId: async (
    orderId: string
  ): Promise<SowingInstruction[]> => {
    const { data } = await apiClient.get<ApiResponse<SowingInstruction[]>>(
      `/SowingInstructions/order/${orderId}`
    );
    return data.data;
  },

  getSowingInstructionById: async (id: string): Promise<SowingInstruction> => {
    const { data } = await apiClient.get<ApiResponse<SowingInstruction>>(
      `/SowingInstructions/${id}`
    );
    return data.data;
  },

  getSowingInstructionByNumber: async (
    instructionNumber: string
  ): Promise<SowingInstructionDto> => {
    const { data } = await apiClient.get<ApiResponse<SowingInstructionDto>>(
      `/SowingInstructions/instruction-number/${instructionNumber}`
    );
    return data.data;
  },

  approveSowingInstruction: async (
    payload: ApproveSowingInstructionRequest
  ): Promise<SowingInstruction> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstruction>>(
      `/SowingInstructions/${payload.sowingInstructionId}/approve`,
      payload
    );
    return data.data;
  },

  revokeSowingInstruction: async (
    payload: RevokeSowingInstructionRequest
  ): Promise<SowingInstruction> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstruction>>(
      `/SowingInstructions/${payload.sowingInstructionId}/revoke`,
      payload
    );
    return data.data;
  },

  recordSowing: async (
    payload: RecordSowingRequest,
    idempotencyKey: string = randomId()
  ): Promise<SowingInstruction> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstruction>>(
      `/SowingInstructions/${payload.sowingInstructionId}/record-sowing`,
      payload,
      { headers: { "Idempotency-Key": idempotencyKey } }
    );
    return data.data;
  },

  recordGermination: async (
    payload: RecordGerminationRequest
  ): Promise<SowingInstruction> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstruction>>(
      `/SowingInstructions/${payload.sowingInstructionId}/record-germination`,
      payload
    );
    return data.data;
  },

  recordGrowth: async (
    payload: RecordGrowthRequest
  ): Promise<SowingInstruction> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstruction>>(
      `/SowingInstructions/${payload.sowingInstructionId}/record-growth`,
      payload
    );
    return data.data;
  },

  recordReadyForDispatch: async (
    payload: RecordReadyForDispatchRequest
  ): Promise<SowingInstruction> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstruction>>(
      `/SowingInstructions/${payload.sowingInstructionId}/record-ready-for-dispatch`,
      payload
    );
    return data.data;
  },

  recordHarvest: async (
    payload: RecordHarvestRequest
  ): Promise<SowingInstruction> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstruction>>(
      `/SowingInstructions/${payload.sowingInstructionId}/record-harvest`,
      payload
    );
    return data.data;
  },

  markOvergrown: async (
    payload: MarkOvergrownRequest
  ): Promise<SowingInstruction> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstruction>>(
      `/SowingInstructions/${payload.sowingInstructionId}/mark-overgrown`,
      payload
    );
    return data.data;
  },

  putOnHold: async (payload: PutOnHoldRequest): Promise<SowingInstruction> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstruction>>(
      `/SowingInstructions/${payload.sowingInstructionId}/put-on-hold`,
      payload
    );
    return data.data;
  },

  resumeFromHold: async (
    payload: ResumeFromHoldRequest
  ): Promise<SowingInstruction> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstruction>>(
      `/SowingInstructions/${payload.sowingInstructionId}/resume-from-hold`,
      payload
    );
    return data.data;
  },

  // ── Phase-workflow endpoints ──────────────────────────────────────────────

  salesCreateDraft: async (
    payload: SalesSowingInstructionRequest
  ): Promise<SowingInstructionDto> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstructionDto>>(
      "/SowingInstructions/draft",
      payload
    );
    return data.data;
  },

  submitSowingInstruction: async (
    id: string,
    payload: SubmitSowingInstructionRequest
  ): Promise<SowingInstructionDto> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstructionDto>>(
      `/SowingInstructions/${id}/submit`,
      payload
    );
    return data.data;
  },

  acceptSowingInstruction: async (
    id: string,
    payload: AcceptSowingInstructionRequest
  ): Promise<SowingInstructionDto> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstructionDto>>(
      `/SowingInstructions/${id}/accept`,
      payload
    );
    return data.data;
  },

  acknowledgeReceipt: async (
    id: string,
    payload: AcknowledgeReceiptRequest
  ): Promise<SowingInstructionDto> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstructionDto>>(
      `/SowingInstructions/${id}/acknowledge`,
      payload
    );
    return data.data;
  },

  getGerminationSplitPreview: async (
    id: string,
    qty: number
  ): Promise<GerminationSplitPreviewDto> => {
    const { data } = await apiClient.get<ApiResponse<GerminationSplitPreviewDto>>(
      `/SowingInstructions/${id}/germination-split-preview?qty=${qty}`
    );
    return data.data;
  },

  getPendingAcceptanceQueue: async (): Promise<SowingInstructionDto[]> => {
    const { data } = await apiClient.get<ApiResponse<SowingInstructionDto[]>>(
      "/SowingInstructions/pending-acceptance"
    );
    return data.data ?? [];
  },

  getReadyForGrowingQueue: async (): Promise<SowingInstructionDto[]> => {
    const { data } = await apiClient.get<ApiResponse<SowingInstructionDto[]>>(
      "/SowingInstructions/ready-for-growing"
    );
    return data.data ?? [];
  },

  recordMaterialUsage: async (
    payload: RecordMaterialUsageRequest
  ): Promise<SowingMaterialUsageLogDto> => {
    const { data } = await apiClient.post<ApiResponse<SowingMaterialUsageLogDto>>(
      "/sowing-material-usage",
      payload
    );
    return data.data;
  },

  getMaterialUsageByDate: async (
    date: string,
    options?: { mineOnly?: boolean; departmentId?: string; recordedByUserId?: string }
  ): Promise<SowingMaterialUsageLogDto[]> => {
    const params = new URLSearchParams({ date });
    if (options?.mineOnly) params.set("mineOnly", "true");
    if (options?.departmentId) params.set("departmentId", options.departmentId);
    if (options?.recordedByUserId) params.set("recordedByUserId", options.recordedByUserId);
    const { data } = await apiClient.get<ApiResponse<SowingMaterialUsageLogDto[]>>(
      `/sowing-material-usage?${params.toString()}`
    );
    return data.data ?? [];
  },

  getMaterialUsageActivityTypes: async (
    search?: string
  ): Promise<MaterialUsageActivityType[]> => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    const { data } = await apiClient.get<ApiResponse<MaterialUsageActivityType[]>>(
      `/sowing-material-usage/activity-types${params.toString() ? `?${params.toString()}` : ""}`
    );
    return data.data ?? [];
  },

  searchMaterialUsageFarms: async (
    search?: string
  ): Promise<MaterialUsageLookupOption[]> => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    const { data } = await apiClient.get<ApiResponse<MaterialUsageLookupOption[]>>(
      `/sowing-material-usage/farms${params.toString() ? `?${params.toString()}` : ""}`
    );
    return data.data ?? [];
  },

  searchMaterialUsageFields: async (
    farmId?: string,
    search?: string
  ): Promise<MaterialUsageLookupOption[]> => {
    const params = new URLSearchParams();
    if (farmId) params.set("farmId", farmId);
    if (search) params.set("search", search);
    const { data } = await apiClient.get<ApiResponse<MaterialUsageLookupOption[]>>(
      `/sowing-material-usage/fields${params.toString() ? `?${params.toString()}` : ""}`
    );
    return data.data ?? [];
  },

  getGerminationChambers: async (): Promise<GerminationChamberDto[]> => {
    const { data } = await apiClient.get<ApiResponse<GerminationChamberDto[]>>("/germination-chambers");
    return data.data ?? [];
  },

  getAllGreenhouseStockEntries: async (): Promise<GreenhouseStockEntryDto[]> => {
    const { data } = await apiClient.get<ApiResponse<GreenhouseStockEntryDto[]>>(
      "/greenhouse-stock/entries"
    );
    return data.data ?? [];
  },

  getAvailableGreenhouseStock: async (params?: {
    cropTypeId?: string;
    variety?: string;
    trayType?: number;
    search?: string;
    readyForWalkIn?: boolean;
    source?: "Greenhouse" | "Agroforestry";
  }): Promise<AvailableGreenhouseStockDto[]> => {
    const query = new URLSearchParams();
    if (params?.cropTypeId) query.set("cropTypeId", params.cropTypeId);
    if (params?.variety) query.set("variety", params.variety);
    if (params?.trayType) query.set("trayType", String(params.trayType));
    if (params?.search) query.set("search", params.search);
    if (params?.readyForWalkIn) query.set("readyForWalkIn", "true");
    if (params?.source) query.set("source", params.source);
    const { data } = await apiClient.get<ApiResponse<AvailableGreenhouseStockDto[]>>(
      `/greenhouse-stock/entries/available?${query}`
    );
    return data.data ?? [];
  },

  getPagedAvailableGreenhouseStock: async (params: {
    cropTypeId?: string;
    variety?: string;
    trayType?: number;
    search?: string;
    pageNumber: number;
    pageSize: number;
    readyForWalkIn?: boolean;
    source?: "Greenhouse" | "Agroforestry";
  }): Promise<PagedGreenhouseStock> => {
    const query = new URLSearchParams();
    if (params.cropTypeId) query.set("cropTypeId", params.cropTypeId);
    if (params.variety) query.set("variety", params.variety);
    if (params.trayType) query.set("trayType", String(params.trayType));
    if (params.search) query.set("search", params.search);
    query.set("pageNumber", String(params.pageNumber));
    query.set("pageSize", String(params.pageSize));
    if (params.readyForWalkIn) query.set("readyForWalkIn", "true");
    if (params.source) query.set("source", params.source);
    const { data } = await apiClient.get<ApiResponse<PagedGreenhouseStock>>(
      `/greenhouse-stock/entries/available?${query}`
    );
    return data.data;
  },

  /** Quantity uses the explicit order item's commercial units, regardless of source units. */
  reserveGreenhouseStock: async (
    entryId: string,
    orderId: string,
    quantity: number,
    orderItemId: string
  ): Promise<GreenhouseStockEntryDto> => {
    const { data } = await apiClient.post<ApiResponse<GreenhouseStockEntryDto>>(
      `/greenhouse-stock/entries/${entryId}/reserve`,
      { orderId, orderItemId, quantity }
    );
    return data.data;
  },

  // ── Bulk import (greenhouse stock at go-live) ──────────────────────────
  downloadImportTemplate: async (): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(
      "/SowingInstructions/import/template",
      { responseType: "blob" }
    );
    return data;
  },

  importStock: async (file: File): Promise<SowingInstructionImportResult> => {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await apiClient.post<ApiResponse<SowingInstructionImportResult>>(
      "/SowingInstructions/import",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return data.data;
  },
};

export interface SowingInstructionImportError {
  rowNumber: number;
  instructionNumber: string | null;
  stage: string | null;
  errors: string[];
}

export interface ImportedSowingInstructionSummary {
  id: string;
  instructionNumber: string;
  stage: string;
  cropType: string;
  variety: string | null;
  quantityToSow: number;
  greenhouseStockEntryId: string | null;
}

export interface SowingInstructionImportResult {
  totalRows: number;
  successCount: number;
  failureCount: number;
  stockEntriesCreated: number;
  errors: SowingInstructionImportError[];
  importedRows: ImportedSowingInstructionSummary[];
}
